import {
  BRIDGE_MESSAGE_SOURCES,
  BRIDGE_MESSAGE_TARGETS,
  createBridgeEnvelope,
} from "@bezoned/desktop-bridge/bridge-common.js";
import { TABLE_CALL_MESSAGE_TYPES } from "@bezoned/desktop-bridge/table-call-messaging.js";
import type {
  TableCallParticipantUpdatePayload,
  TableCallSelfUpdatePayload,
} from "@bezoned/desktop-bridge/table-call-messaging.js";

import { logTableCallBridgeMessage } from "./debug-log.js";

const createDeltaEnvelope = (
  type: string,
  instanceId: string,
  payload: unknown
) =>
  createBridgeEnvelope({
    instanceId,
    payload,
    source: BRIDGE_MESSAGE_SOURCES.FRONTEND,
    target: BRIDGE_MESSAGE_TARGETS.DESKTOP,
    type,
  });

export const createTableCallDeltaSenders = ({
  instanceId,
  bridgeRoom,
  nextProjectionSequence,
  sendJson,
}: {
  instanceId: string | null;
  bridgeRoom: string;
  nextProjectionSequence: () => number;
  sendJson: (room: string, data: unknown) => void;
}) => {
  const sendDelta = (type: string, payload: object) => {
    if (!instanceId) {
      return;
    }

    const message = createDeltaEnvelope(type, instanceId, {
      ...payload,
      projectionSequence: nextProjectionSequence(),
    });
    logTableCallBridgeMessage({
      direction: "outbound",
      message,
      room: bridgeRoom,
    });
    sendJson(bridgeRoom, message);
  };

  const sendTableCallState = (payload: object) => {
    sendDelta(TABLE_CALL_MESSAGE_TYPES.STATE, payload);
  };

  const sendSpeakingChange = ({
    userId,
    isSpeaking,
    volumeLevel,
  }: {
    userId: string;
    isSpeaking: boolean;
    volumeLevel: number;
  }) => {
    sendDelta(TABLE_CALL_MESSAGE_TYPES.SPEAKING_CHANGE, {
      isSpeaking,
      userId,
      volumeLevel,
    });
  };

  const sendSelfUpdate = (
    payload: Omit<TableCallSelfUpdatePayload, "projectionSequence">
  ) => {
    sendDelta(TABLE_CALL_MESSAGE_TYPES.SELF_UPDATE, payload);
  };

  const sendParticipantUpdate = (
    payload: Omit<TableCallParticipantUpdatePayload, "projectionSequence">
  ) => {
    sendDelta(TABLE_CALL_MESSAGE_TYPES.PARTICIPANT_UPDATE, payload);
  };

  const sendTableCallDisconnected = (reason: string) => {
    sendDelta(TABLE_CALL_MESSAGE_TYPES.DISCONNECTED, { reason });
  };

  return {
    sendParticipantUpdate,
    sendSelfUpdate,
    sendSpeakingChange,
    sendTableCallDisconnected,
    sendTableCallState,
  };
};
